import { memo } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

const proBadgeVariants = cva('flex items-center justify-center', {
  variants: {
    size: {
      sm: 'gap-0.5 px-1.5 py-0.5',
      md: 'gap-1 px-2 py-1',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

interface ProBadgeProps extends VariantProps<typeof proBadgeVariants> {
  /** The "requires Pro" markers keep the PRO default. Plan cards pass their tier. */
  label?: string
}

export const ProBadge = memo(({ size = 'md', label = 'PRO' }: ProBadgeProps) => {
  const iconSize = size === 'sm' ? 9 : 11
  const borderRadius = size === 'sm' ? '33px' : '47px'
  const fontSize = size === 'sm' ? '9px' : '11px'
  const letterSpacing = size === 'sm' ? '-0.13px' : '-0.17px'
  const boxShadow =
    size === 'sm'
      ? '0 0 2px 0.5px rgba(255, 255, 255, 0.18) inset, 0 0 6px 0.5px rgba(255, 255, 255, 0.18) inset, 0 0 6px 3px rgba(0, 0, 0, 0.07)'
      : '0 0 2px 0.5px rgba(255, 255, 255, 0.18) inset, 0 0 8px 1px rgba(255, 255, 255, 0.18) inset, 0 0 8px 4px rgba(0, 0, 0, 0.07)'
  const backdropFilter = size === 'sm' ? 'blur(18px)' : 'blur(23px)'

  return (
    <div
      className={twMerge(proBadgeVariants({ size }), 'bg-foreground text-background')}
      style={{
        borderRadius,
        boxShadow,
        backdropFilter,
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 11 11" fill="none">
        <foreignObject x="-0.980222" y="-0.976743" width="12.6174" height="12.6175">
          <div
            style={{
              backdropFilter: 'blur(0.75px)',
              clipPath: 'url(#bgblur_0_2610_48304_clip_path)',
              height: '100%',
              width: '100%',
            }}
          />
        </foreignObject>
        <g filter="url(#filter0_dii_2610_48304)" data-figma-bg-blur-radius="1.50799">
          <path
            d="M4.85667 0.923082C4.87723 0.812982 4.93566 0.713541 5.02182 0.641982C5.10798 0.570422 5.21646 0.53125 5.32846 0.53125C5.44047 0.53125 5.54894 0.570422 5.63511 0.641982C5.72127 0.713541 5.77969 0.812982 5.80026 0.923082L6.30469 3.59067C6.34052 3.78032 6.43268 3.95477 6.56916 4.09125C6.70564 4.22772 6.88008 4.31989 7.06974 4.35571L9.73732 4.86015C9.84742 4.88071 9.94686 4.93913 10.0184 5.0253C10.09 5.11146 10.1292 5.21994 10.1292 5.33194C10.1292 5.44395 10.09 5.55242 10.0184 5.63859C9.94686 5.72475 9.84742 5.78317 9.73732 5.80374L7.06974 6.30817C6.88008 6.344 6.70564 6.43616 6.56916 6.57264C6.43268 6.70912 6.34052 6.88356 6.30469 7.07322L5.80026 9.7408C5.77969 9.8509 5.72127 9.95034 5.63511 10.0219C5.54894 10.0935 5.44047 10.1326 5.32846 10.1326C5.21646 10.1326 5.10798 10.0935 5.02182 10.0219C4.93566 9.95034 4.87723 9.8509 4.85667 9.7408L4.35224 7.07322C4.31641 6.88356 4.22424 6.70912 4.08777 6.57264C3.95129 6.43616 3.77684 6.344 3.58719 6.30817L0.919603 5.80374C0.809503 5.78317 0.710062 5.72475 0.638503 5.63859C0.566943 5.55242 0.527771 5.44395 0.527771 5.33194C0.527771 5.21994 0.566943 5.11146 0.638503 5.0253C0.710062 4.93913 0.809503 4.88071 0.919603 4.86015L3.58719 4.35571C3.77684 4.31989 3.95129 4.22772 4.08777 4.09125C4.22424 3.95477 4.31641 3.78032 4.35224 3.59067L4.85667 0.923082Z"
            fill="currentColor"
            fillOpacity=""
            shapeRendering="crispEdges"
          />
        </g>
        <defs>
          <filter
            id="filter0_dii_2610_48304"
            x="-0.980222"
            y="-0.976743"
            width="12.6174"
            height="12.6175"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology radius="0.150799" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2610_48304" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.188499" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2610_48304" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2610_48304" result="shape" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology radius="0.226199" operator="erode" in="SourceAlpha" result="effect2_innerShadow_2610_48304" />
            <feOffset />
            <feGaussianBlur stdDeviation="0.188499" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0" />
            <feBlend mode="normal" in2="shape" result="effect2_innerShadow_2610_48304" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology
              radius="0.0376998"
              operator="erode"
              in="SourceAlpha"
              result="effect3_innerShadow_2610_48304"
            />
            <feOffset />
            <feGaussianBlur stdDeviation="0.0376998" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0" />
            <feBlend mode="normal" in2="effect2_innerShadow_2610_48304" result="effect3_innerShadow_2610_48304" />
          </filter>
          <clipPath id="bgblur_0_2610_48304_clip_path" transform="translate(0.980222 0.976743)">
            <path d="M4.85667 0.923082C4.87723 0.812982 4.93566 0.713541 5.02182 0.641982C5.10798 0.570422 5.21646 0.53125 5.32846 0.53125C5.44047 0.53125 5.54894 0.570422 5.63511 0.641982C5.72127 0.713541 5.77969 0.812982 5.80026 0.923082L6.30469 3.59067C6.34052 3.78032 6.43268 3.95477 6.56916 4.09125C6.70564 4.22772 6.88008 4.31989 7.06974 4.35571L9.73732 4.86015C9.84742 4.88071 9.94686 4.93913 10.0184 5.0253C10.09 5.11146 10.1292 5.21994 10.1292 5.33194C10.1292 5.44395 10.09 5.55242 10.0184 5.63859C9.94686 5.72475 9.84742 5.78317 9.73732 5.80374L7.06974 6.30817C6.88008 6.344 6.70564 6.43616 6.56916 6.57264C6.43268 6.70912 6.34052 6.88356 6.30469 7.07322L5.80026 9.7408C5.77969 9.8509 5.72127 9.95034 5.63511 10.0219C5.54894 10.0935 5.44047 10.1326 5.32846 10.1326C5.21646 10.1326 5.10798 10.0935 5.02182 10.0219C4.93566 9.95034 4.87723 9.8509 4.85667 9.7408L4.35224 7.07322C4.31641 6.88356 4.22424 6.70912 4.08777 6.57264C3.95129 6.43616 3.77684 6.344 3.58719 6.30817L0.919603 5.80374C0.809503 5.78317 0.710062 5.72475 0.638503 5.63859C0.566943 5.55242 0.527771 5.44395 0.527771 5.33194C0.527771 5.21994 0.566943 5.11146 0.638503 5.0253C0.710062 4.93913 0.809503 4.88071 0.919603 4.86015L3.58719 4.35571C3.77684 4.31989 3.95129 4.22772 4.08777 4.09125C4.22424 3.95477 4.31641 3.78032 4.35224 3.59067L4.85667 0.923082Z" />
          </clipPath>
        </defs>
      </svg>
      <span
        className="text-background font-semibold leading-[170%]"
        style={{
          fontFamily: 'Aeonik',
          fontSize,
          letterSpacing,
        }}
      >
        {label}
      </span>
    </div>
  )
})
ProBadge.displayName = 'ProBadge'
